import { api } from "../lib/api";
import type {
	Document,
	DocumentAnalysis,
	DocumentCategory,
	DocumentFilters,
	DocumentShare,
	DocumentsResponse,
} from "../types/document";

/**
 * Get all documents for the current user with optional filters
 */
export async function getAllDocuments(
	filters?: DocumentFilters,
): Promise<DocumentsResponse> {
	const params = new URLSearchParams();

	if (filters?.search) params.append("search", filters.search);
	if (filters?.category) params.append("category", filters.category);
	if (filters?.sharedOnly) params.append("sharedOnly", "true");
	if (filters?.page) params.append("page", filters.page.toString());
	if (filters?.limit) params.append("limit", filters.limit.toString());

	const response = await api.get<DocumentsResponse>(
		`/api/documents?${params.toString()}`,
	);
	return response.data;
}

/**
 * Get a single document by ID
 */
export async function getDocumentById(
	documentId: string,
): Promise<{ document: Document }> {
	const response = await api.get<{ document: Document }>(
		`/api/documents/${documentId}`,
	);
	return response.data;
}

/**
 * Upload a new document
 */
export async function uploadDocument(
	formData: FormData,
	onProgress?: (progress: number) => void,
): Promise<{ success: boolean; document: Document }> {
	const response = await api.post<{ success: boolean; document: Document }>(
		"/api/documents/upload",
		formData,
		{
			headers: {
				"Content-Type": "multipart/form-data",
			},
			onUploadProgress: (progressEvent) => {
				if (onProgress && progressEvent.total) {
					const progress = Math.round(
						(progressEvent.loaded * 100) / progressEvent.total,
					);
					onProgress(progress);
				}
			},
		},
	);
	return response.data;
}

/**
 * Delete a document
 */
export async function deleteDocument(
	documentId: string,
): Promise<{ success: boolean }> {
	const response = await api.delete<{ success: boolean }>(
		`/api/documents/${documentId}`,
	);
	return response.data;
}

/**
 * Update document category
 */
export async function updateDocumentCategory(
	documentId: string,
	category: DocumentCategory,
): Promise<{ success: boolean; document: Document }> {
	const response = await api.patch<{ success: boolean; document: Document }>(
		`/api/documents/${documentId}/category`,
		{ category },
	);
	return response.data;
}

/**
 * Get document shares
 */
export async function getDocumentShares(
	documentId: string,
): Promise<{ shares: DocumentShare[] }> {
	const response = await api.get<{ shares: DocumentShare[] }>(
		`/api/documents/${documentId}/share`,
	);
	return response.data;
}

/**
 * Share document with providers
 */
export async function shareDocument(
	documentId: string,
	providerIds: string[],
): Promise<{ success: boolean; shares: DocumentShare[] }> {
	const response = await api.post<{
		success: boolean;
		shares: DocumentShare[];
	}>(`/api/documents/${documentId}/share`, { providerIds });
	return response.data;
}

/**
 * Revoke document share
 */
export async function revokeDocumentShare(
	documentId: string,
	providerId: string,
): Promise<{ success: boolean }> {
	const response = await api.delete<{ success: boolean }>(
		`/api/documents/${documentId}/share?providerId=${providerId}`,
	);
	return response.data;
}

/**
 * Get a signed URL for viewing/downloading a document
 */
export async function getDocumentViewUrl(
	documentId: string,
	download = false,
): Promise<{ url: string }> {
	const response = await api.get<{ url: string }>(
		`/api/documents/${documentId}/view-url`,
		{ params: download ? { download: "true" } : undefined },
	);
	return response.data;
}

/**
 * Get document analysis
 */
export async function getDocumentAnalysis(
	documentId: string,
): Promise<{ analyses: DocumentAnalysis[] }> {
	const response = await api.get<{ analyses: DocumentAnalysis[] }>(
		`/api/documents/${documentId}/analyze`,
	);
	return response.data;
}

/**
 * Trigger document analysis
 */
export async function analyzeDocument(
	documentId: string,
): Promise<{ success: boolean; analysis: DocumentAnalysis }> {
	const response = await api.post<{
		success: boolean;
		analysis: DocumentAnalysis;
	}>(`/api/documents/${documentId}/analyze`);
	return response.data;
}
